import React from "react";
import { motion } from "framer-motion";
import StickyNavbar from "../navbar";
import { Link } from "react-router-dom";

const Chapter5 = () => {
  return (
    <div>
      <StickyNavbar />
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="bg-white text-black p-4 lg:mt-10 mt-20 lg:text-lg sm:p-10 lg:px-20"
      >
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          
          <p className="text-[#005FFF] py-5 text-2xl">
            Intended Behavioural Outcome (IBO)
          </p>
          <p>
            The Community Health Extension Worker (WHO) will select and use the
            right research methods (WHAT) for the health issue they are
            researching in their community (WHERE). They will do this by
            understanding the different approaches to research, including
            qualitative, quantitative, and mixed methods, and the different data
            collection tools they can use (HOW) to collect data from their
            participants (WITH WHOM).
          </p>
          <p>
            Research methods are the tools and ways we collect and study our
            data. There will be methods of doing research which are more helpful
            to use for the health issue or group of people that you are
            researching. In this chapter, we will look at some of the main
            methods, ways of collecting data, ways of analysing your data, ways
            of telling a story about your data, and ways of presenting it.
          </p>
        </motion.div>

        {/* Content Sections */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Research Methods Approach */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="space-y-3"
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">
              Approach to research methods
            </h2>
            <p>
              It is not the case that one way of doing research is better than
              the other, and often a project can use more than one approach at
              different stages(75). However, using different approaches will
              give us different types of information, helping us to understand a
              health issue in different ways(76). Let’s go back to our previous
              example of understanding the problems that people face in
              accessing child and maternal healthcare to help us:
            </p>
            <ul className="space-y-3">
              <li>
                <span className="text-[#005FFF]">❖</span> Qualitative research:
                Qualitative research helps us understand peoples experiences,
                opinions, and feelings. It can help us explore the ‘why’ and
                ‘how’ behind certain behaviours and choices. For example, we
                could talk directly with mothers and ask them about their
                experiences of accessing healthcare. We might also ask a small
                group of mothers about the problems they face; such as
                transportation issues, costs, or cultural beliefs that may stop
                them from seeking care. In this way we can learn about their
                lived experience. If we do not understand an issue very well
                yet, doing qualitative research is a good place to begin, before
                collecting large amounts of data about large amounts of
                people.(77-79)
              </li>
              <li>
                <span className="text-[#005FFF]">❖</span> Quantitative research:
                Quantitative research means to collect data in the form of
                numbers. Quantitative research is usually done with bigger
                groups of people and collects a larger amount of data, so it can
                be very good at helping us to see the scale of the problem. For
                instance, we could collect data on the number of mothers who
                have attended care visits at health clinics, or the number of
                children in the community who have been vaccinated against a
                disease. Instead of looking at people’s personal stories and
                experiences, this way of doing research can help us to see
                patterns across larger amounts of people. (80, 81)
              </li>
              <li>
                <span className="text-[#005FFF]">❖</span> Mixed methods: Using a
                mixed methods approach means to bring together both numbers
                based (quantitative) and story based (qualitative) research. For
                example we could start by collecting number data on the amount
                of households in the community who are attending care check-ups
                (quantitative data), and how often they are accessing this care.
                Then as part of the same project, we might do more research with
                some of those households and ask them more questions about why
                certain barriers, like transportation issues or long wait times,
                make it hard for them to get care (qualitative data). Doing both
                allows us to both measure and understand the problem, giving a
                bigger picture of what’s happening, and why.(82, 83)
              </li>
            </ul>
          </motion.div>

          {/* Sampling Section */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="space-y-3"
          >
            <h2 className="text-xl font-bold mb-2 text-[#005FFF]">Sampling</h2>
            <p>
              The term ‘sampling’ simply means the way in which you choose which
              people from the community are going to be a part of your study.
              The way you choose this group can affect what you learn(84). The
              different ways of sampling are listed below:
            </p>
            <ul className="list-disc list-inside space-y-3">
              <li>
                Random sampling: A way of choosing participants where everyone
                in the population has an equal chance of being selected.
              </li>
              <li>
                Purposeful sampling: A way of choosing participants who have
                certain knowledge or experience related to your research
                question, focusing on those who can provide the most useful
                information.
              </li>
              <li>
                Convenience sampling: A way of choosing participants based on
                how easy or available they are to reach, which is quicker but
                may not always represent the whole community.
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Navigation Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex justify-between mt-10"
        >
          <Link to="/chapter5/video">
            <button className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
              Previous
            </button>
          </Link>
          <Link to="/chapter5/data">
            <button className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
              Next
            </button>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Chapter5;
